import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAccess } from './api/data';
import { api, mutationHeaders, type Schema } from './api/client';
import { apiError, sessionStore } from './api/session';
import { AppearanceSettings } from './appearance-settings';
import { DEFAULT_APPEARANCE_COLOR, normalizeHexColor } from './appearance-colors';
import {
  applyInterfaceColor,
  cacheAppearanceColor,
  readAppearanceCache,
} from './appearance-runtime';
import { ErrorState, Loading, useToast } from './ui';

export function AppearancePage() {
  return (
    <section className="appearance-page">
      <h1>Оформление</h1>
      <AccountAppearanceSettings />
    </section>
  );
}

export function AccountAppearanceSettings() {
  const access = useAccess();
  const client = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const savedColor = normalizeHexColor(access.data?.appearance?.color) || DEFAULT_APPEARANCE_COLOR;

  useEffect(
    () => () => {
      const account = sessionStore.getSnapshot().account;
      const current = client.getQueryData<Schema<'CurrentUserResponse'>>(['access']);
      const color =
        account &&
        current?.account.id === account.id &&
        current.account.workspaceId === account.workspaceId
          ? current.appearance?.color
          : account
            ? readAppearanceCache(account)
            : null;
      applyInterfaceColor(color);
    },
    [client],
  );

  if (access.isPending) return <Loading />;
  if (access.error || !access.data) return <ErrorState error={access.error} />;
  const owner = access.data.account;
  return (
    <AppearanceSettings
      color={draft || savedColor}
      savedColor={savedColor}
      saving={saving}
      error={error}
      onPreview={(color) => {
        setDraft(color);
        setError(null);
        applyInterfaceColor(color);
      }}
      onCancel={() => {
        setDraft(null);
        setError(null);
        applyInterfaceColor(savedColor);
      }}
      onSave={async (color) => {
        const normalized = normalizeHexColor(color);
        if (!normalized) return;
        setSaving(true);
        setError(null);
        try {
          const result = await api.PATCH('/api/v1/me/appearance', {
            params: { header: mutationHeaders() },
            body: { color: normalized === DEFAULT_APPEARANCE_COLOR ? null : normalized },
          });
          if (!result.data) throw apiError(result.response, result.error);
          const active = sessionStore.getSnapshot().account;
          if (active?.id !== owner.id || active.workspaceId !== owner.workspaceId) return;
          await client.cancelQueries({ queryKey: ['access'] });
          const current = client.getQueryData<Schema<'CurrentUserResponse'>>(['access']);
          if (current?.account.id !== owner.id || current.account.workspaceId !== owner.workspaceId)
            return;
          client.setQueryData<Schema<'CurrentUserResponse'>>(['access'], {
            ...current,
            appearance: result.data,
          });
          cacheAppearanceColor(owner, result.data.color);
          applyInterfaceColor(result.data.color);
          setDraft(null);
          toast('Цвет интерфейса сохранён');
        } catch (cause) {
          setError(cause instanceof Error ? cause : new Error('Не удалось сохранить цвет'));
        } finally {
          setSaving(false);
        }
      }}
    />
  );
}
